import { initExitIntent } from './aas-exit-intent'

/* Team marketing page: commander/operator toggle, routing picker,
   testimonials carousel, and assessment stub. Illustrative only. */

export type RoutingSide = { price: string; model: string }
export type RoutingTask = {
  label: string
  frontier: RoutingSide
  routed: RoutingSide
  note: string
}
export type Testimonial = {
  title: string
  quote: string
  name: string
  role: string
}
export type ViewMode = 'commander' | 'operator'

export const ROUTING_TASKS: Record<string, RoutingTask> = {
  "sort-tickets": {
    label: "Sort 40 support tickets",
    frontier: { price: "$4 / $20", model: "Claude Opus 5.5" },
    routed: { price: "$0.10 / $0.50", model: "GPT-6 Luna" },
    note: "Sorting 40 support tickets costs 40× less when it's routed to the right model. Same result, and the frontier stays available for the work that needs it.",
  },
  "summarize-churn": {
    label: "Summarize churn calls",
    frontier: { price: "$4 / $20", model: "Claude Opus 5.5" },
    routed: { price: "$0.10 / $0.50", model: "GPT-6 Luna" },
    note: "Summarizing churn calls costs 40× less when it's routed to the right model. Same result, and the frontier stays available for the work that needs it.",
  },
  "translate-help": {
    label: "Translate the help center",
    frontier: { price: "$4 / $20", model: "Claude Opus 5.5" },
    routed: { price: "$0.10 / $0.50", model: "Gemini 3.8 Flash" },
    note: "Translating the help center costs 40× less when it's routed to the right model. Same result, and the frontier stays available for the work that needs it.",
  },
  "scan-replies": {
    label: "Scan launch replies",
    frontier: { price: "$4 / $20", model: "Claude Opus 5.5" },
    routed: { price: "$0.10 / $0.50", model: "GPT-6 Luna" },
    note: "Scanning launch replies costs 40× less when it's routed to the right model. Same result, and the frontier stays available for the work that needs it.",
  },
  "read-contract": {
    label: "Read a 400-page contract",
    frontier: { price: "$4 / $20", model: "Claude Opus 5.5" },
    routed: { price: "$0.50 / $2.50", model: "Gemini 3.8 Flash" },
    note: "Reading a 400-page contract costs 8× less when long context runs on the right model. Same result, and the frontier stays available for the work that needs it.",
  },
  "refactor-billing": {
    label: "Refactor the billing service",
    frontier: { price: "$4 / $20", model: "Claude Opus 5.5" },
    routed: { price: "$1.00 / $5.00", model: "Kimi K2.7 Code" },
    note: "Refactoring the billing service still earns a strong model, but routing keeps routine edits off the frontier at a fraction of the list price.",
  },
};

export const TESTIMONIALS: Testimonial[] = [];

export const TEAM_SIZES: string[] = ["10–25", "26–50", "51–100", "101–250", "250+"];

export function getRoutingTask(id: string): RoutingTask {
  return ROUTING_TASKS[id] ?? ROUTING_TASKS["sort-tickets"];
}

export function nextCarouselIndex(current: number, total: number, delta = 1): number {
  if (!total) return 0;
  return ((current + delta) % total + total) % total;
}

export function toggleViewMode(current: ViewMode): ViewMode {
  return current === "commander" ? "operator" : "commander";
}

export function applyViewMode(root: ParentNode, mode: ViewMode): ViewMode {
  const panel = root.querySelector("[data-aas-view-panel]");
  const buttons = root.querySelectorAll("[data-aas-view]");
  if (panel) (panel as HTMLElement).dataset.mode = mode;
  buttons.forEach((node) => {
    const btn = node as HTMLButtonElement
    const active = btn.dataset.aasView === mode;
    btn.setAttribute("aria-selected", active ? "true" : "false");
    btn.classList.toggle("is-active", active);
    btn.tabIndex = active ? 0 : -1;
  });
  const commander = root.querySelector("[data-aas-commander]");
  const operator = root.querySelector("[data-aas-operator]");
  if (commander) (commander as HTMLElement).hidden = mode !== "commander";
  if (operator) (operator as HTMLElement).hidden = mode !== "operator";
  return mode;
}

export function applyRoutingTask(root: ParentNode, taskId: string): string {
  const task = getRoutingTask(taskId);
  root.querySelectorAll("[data-aas-task]").forEach((node) => {
    const btn = node as HTMLButtonElement
    const active = btn.dataset.aasTask === taskId;
    btn.setAttribute("aria-pressed", active ? "true" : "false");
    btn.classList.toggle("is-active", active);
  });
  const frontierPrice = root.querySelector("[data-aas-frontier-price]");
  const frontierModel = root.querySelector("[data-aas-frontier-model]");
  const routedPrice = root.querySelector("[data-aas-routed-price]");
  const routedModel = root.querySelector("[data-aas-routed-model]");
  const note = root.querySelector("[data-aas-routing-note]");
  if (frontierPrice) frontierPrice.textContent = task.frontier.price;
  if (frontierModel) frontierModel.textContent = task.frontier.model;
  if (routedPrice) routedPrice.textContent = task.routed.price;
  if (routedModel) routedModel.textContent = task.routed.model;
  if (note) note.textContent = task.note;
  return taskId;
}

export function applyTestimonial(root: ParentNode, index: number): number {
  const total = TESTIMONIALS.length;
  if (!total) return 0;
  const i = nextCarouselIndex(index, total, 0);
  const item = TESTIMONIALS[i]!;
  const title = root.querySelector("[data-aas-quote-title]");
  const quote = root.querySelector("[data-aas-quote]");
  const name = root.querySelector("[data-aas-quote-name]");
  const role = root.querySelector("[data-aas-quote-role]");
  if (title) title.textContent = `“${item.title}”`;
  if (quote) quote.textContent = item.quote;
  if (name) name.textContent = item.name;
  if (role) role.textContent = item.role;
  root.querySelectorAll("[data-aas-dot]").forEach((node) => {
    const dot = node as HTMLButtonElement
    const active = Number(dot.dataset.aasDot) === i;
    dot.classList.toggle("is-active", active);
    dot.setAttribute("aria-current", active ? "true" : "false");
  });
  return i;
}

export function applyAssessmentSize(root: ParentNode, size: string): string {
  root.querySelectorAll("[data-aas-size]").forEach((node) => {
    const btn = node as HTMLButtonElement
    const active = btn.dataset.aasSize === size;
    btn.setAttribute("aria-pressed", active ? "true" : "false");
    btn.classList.toggle("is-active", active);
  });
  const status = root.querySelector("[data-aas-assessment-status]");
  if (status) {
    status.textContent = size
      ? `Team size ${size} selected. We will tailor the assessment to your range.`
      : "Choose a team size to begin the illustrative assessment.";
  }
  return size;
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** Brief enter-through stagger on the TEAMS letter portal. No-op when reduced motion. */
export function playLetterPortal(portal: HTMLElement, letter: string): void {
  if (prefersReducedMotion()) return;
  const labels = [...portal.querySelectorAll("label")] as HTMLElement[];
  const idx = labels.findIndex((label) => {
    const input = label.querySelector("input") as HTMLInputElement | null;
    return input?.value === letter;
  });
  portal.classList.remove("is-entering");
  // Restart CSS animation
  void portal.offsetWidth;
  labels.forEach((label, i) => {
    label.style.setProperty("--aas-letter-i", String(Math.abs(i - Math.max(idx, 0))));
    label.classList.toggle("is-portal-origin", i === idx);
  });
  portal.classList.add("is-entering");
  window.setTimeout(() => portal.classList.remove("is-entering"), 700);
}


/* Pill nav: below 860px the primary links fold into a menu under the bar.
   The menu button opens it; a link, Escape, a click outside, or a wider window closes it. */
export function initAasNav(root: ParentNode = document): void {
  const nav = root.querySelector<HTMLElement>("[data-aas-nav]");
  const toggle = nav?.querySelector<HTMLButtonElement>("[data-aas-nav-toggle]");
  if (!nav || !toggle) return;

  const setOpen = (open: boolean) => {
    toggle.setAttribute("aria-expanded", String(open));
    nav.toggleAttribute("data-open", open);
  };

  toggle.addEventListener("click", () => {
    setOpen(toggle.getAttribute("aria-expanded") !== "true");
  });
  nav.addEventListener("click", (e) => {
    const target = e.target as Element | null;
    if (target?.closest(".aas-nav-links a")) setOpen(false);
  });
  document.addEventListener("click", (e) => {
    if (!nav.contains(e.target as Node)) setOpen(false);
  });
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape" || !nav.hasAttribute("data-open")) return;
    setOpen(false);
    toggle.focus();
  });
  const wide = typeof matchMedia === "function" ? matchMedia("(min-width: 860px)") : null;
  wide?.addEventListener("change", (e) => {
    if (e.matches) setOpen(false);
  });
}

export function initLetterPortal(root: ParentNode = document): void {
  const portal = root.querySelector("[data-aas-letter-portal]") as HTMLElement | null;
  if (!portal) return;
  portal.querySelectorAll('input[name="aas-letter"]').forEach((node) => {
    const input = node as HTMLInputElement;
    input.addEventListener("change", () => {
      if (!input.checked) return;
      playLetterPortal(portal, input.value);
    });
  });
}

export function initAiAtScale(root: ParentNode = document) {
  let mode: ViewMode = "commander";
  let taskId = "sort-tickets";
  let testimonial = 0;
  let teamSize = "";

  applyViewMode(root, mode);
  applyRoutingTask(root, taskId);
  applyTestimonial(root, testimonial);
  applyAssessmentSize(root, teamSize);

  const viewButtons = [...root.querySelectorAll("[data-aas-view]")] as HTMLButtonElement[];
  viewButtons.forEach((btn) => {
    btn.addEventListener("click", () => {
      mode = (btn.dataset.aasView as ViewMode) || toggleViewMode(mode);
      applyViewMode(root, mode);
    });
    btn.addEventListener("keydown", (e: KeyboardEvent) => {
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      e.preventDefault();
      const idx = viewButtons.indexOf(btn);
      const next = viewButtons[(idx + (e.key === "ArrowRight" ? 1 : -1) + viewButtons.length) % viewButtons.length];
      if (!next) return;
      mode = (next.dataset.aasView as ViewMode) || mode;
      applyViewMode(root, mode);
      next.focus();
    });
  });

  root.querySelectorAll("[data-aas-task]").forEach((node) => {
    const btn = node as HTMLButtonElement
    btn.addEventListener("click", () => {
      taskId = btn.dataset.aasTask || taskId;
      applyRoutingTask(root, taskId);
    });
  });

  const prev = root.querySelector("[data-aas-carousel-prev]");
  const next = root.querySelector("[data-aas-carousel-next]");
  if (prev) {
    prev.addEventListener("click", () => {
      testimonial = nextCarouselIndex(testimonial, TESTIMONIALS.length, -1);
      applyTestimonial(root, testimonial);
    });
  }
  if (next) {
    next.addEventListener("click", () => {
      testimonial = nextCarouselIndex(testimonial, TESTIMONIALS.length, 1);
      applyTestimonial(root, testimonial);
    });
  }
  root.querySelectorAll("[data-aas-dot]").forEach((node) => {
    const dot = node as HTMLButtonElement
    dot.addEventListener("click", () => {
      testimonial = Number(dot.dataset.aasDot);
      applyTestimonial(root, testimonial);
    });
  });

  root.querySelectorAll("[data-aas-size]").forEach((node) => {
    const btn = node as HTMLButtonElement
    btn.addEventListener("click", () => {
      teamSize = btn.dataset.aasSize || teamSize;
      applyAssessmentSize(root, teamSize);
    });
  });

  initAasNav(root);
  initLetterPortal(root);

  initExitIntent(root, { primaryHref: '/questionnaire/' });

  return {
    get mode() { return mode; },
    get taskId() { return taskId; },
    get testimonial() { return testimonial; },
    get teamSize() { return teamSize; },
  };
}

if (typeof document !== "undefined") {
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initAiAtScale(document), { once: true });
  } else {
    initAiAtScale(document);
  }
}
